import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OverviewCardComponent, StatCard } from './overview-card/overview-card.component';
import { ExtendedLogsWrapperComponent } from '../admin/extended-logs/extended-logs-wrapper/extended-logs-wrapper.component';
import { CallCenterStatsTableComponent } from '../call-center-stats/call-center-stats-table/call-center-stats-table.component';
import { WeekSelectorComponent } from '../call-center-stats/week-selector/week-selector.component';
import { StatisticsTableComponent } from '../statistic-summary/statistics-table/statistics-table';
import { MonthlySelectorComponent } from '../statistic-summary/monthly-selector/monthly-selector.component';
import { StatusReportTableComponent } from './status-report-table/status-report-table.component';
import { StatusReportRow } from '../../interfaces/status-report.interface';
import { PerformanceMetric } from '../../interfaces/statistics.interface';
import { CallCenterStatsService } from '../../services/call-center-stats.service';
import { SalesStatsService } from '../../services/sales-stats.service';
import { AuthService } from '../../services/auth.service';
import { User } from '../../interfaces/auth.interface';
import { SalesStats } from '../../interfaces/sales-stats.interface';
import { UploadService } from '../../services/upload.service';
import { InteractionsService } from '../../services/interactions.service';
import { BackupService } from '../../services/backup.service';
import { DateService } from '../../services/date.service';
import { WeekInfo, CallCenterStatsData } from '../../interfaces/call-center-stats.interface';

// Constants
const CONSTANTS = {
  ERROR_DURATION: 8000,
  RECENT_RECORDS_LIMIT: 10,
  MINIMUM_PERFORMANCE_THRESHOLD: 80,
  FULL_PERFORMANCE_THRESHOLD: 100,
  WEEKEND_MULTIPLIER: 0.3,
  WEEKEND_MAX_MULTIPLIER: 0.5,
  ALLOWED_ROLES: ['admin', 'sales_manager', 'deputy-CEO'],
  HUNGARIAN_MONTHS: [
    'január', 'február', 'március', 'április', 'május', 'június',
    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
  ]
} as const;

// Types
interface ColleagueWithMetrics {
  colleague: User;
  performanceMetrics: PerformanceMetric[];
}

interface PerformanceThresholds {
  type: 'positive' | 'negative' | 'neutral';
}

@Component({
  selector: 'app-overview',
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatTabsModule,
    MatTooltipModule,
    OverviewCardComponent,
    ExtendedLogsWrapperComponent,
    CallCenterStatsTableComponent,
    WeekSelectorComponent,
    StatisticsTableComponent,
    MonthlySelectorComponent,
    StatusReportTableComponent
],
  templateUrl: './overview.html',
  styleUrl: './overview.scss'
})
export class Overview implements OnInit, AfterViewInit {
  // Public properties
  callCenterUsers: any[] = [];
  selectedWeek: WeekInfo | null = null;
  
  // Loading states
  isLoadingStats = false;
  isLoadingStatistics = true;
  isLoadingStatusReport = false;

  // Státusz riport adatok
  statusReportRows: StatusReportRow[] = [];
  isExportingStatusReport = false;
  
  // Statistics data
  currentMonth: string = CONSTANTS.HUNGARIAN_MONTHS[new Date().getMonth()];
  currentYear = new Date().getFullYear();
  daysInCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  isKumMode = false;
  colleaguesWithMetrics: ColleagueWithMetrics[] = [];
  preloadedStatsMap: { [userId: number]: CallCenterStatsData[] } | null = null;
  preloadedKumStatsMap: { [userId: number]: SalesStats[] } | null = null;
  
  // Statistics cards configurations (mutable for updates)
  adminStats: StatCard[] = [];
  kamStats: StatCard[] = [];
  
  // Dynamic stats (will be updated)
  stats: StatCard[] = [];

  constructor(
    private readonly callCenterStatsService: CallCenterStatsService,
    private readonly salesStatsService: SalesStatsService,
    private readonly authService: AuthService,
    private readonly uploadService: UploadService,
    private readonly interactionsService: InteractionsService,
    private readonly backupService: BackupService,
    private readonly dateService: DateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar
  ) {
    this.initializeStats();
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    // mat-tab-group belső állapot-inicializációja után extra detekció szükséges (NG0100 megelőzése)
    this.cdr.detectChanges();
  }

  // ===== Public Role Check Methods =====
  
  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  isCallCenterEmployee(): boolean {
    return this.authService.isCallCenterEmployee();
  }

  isKAM(): boolean {
    return this.authService.isKAM();
  }

  isSalesSupportLeader(): boolean {
    const currentUser = this.authService.getCurrentUser();
    return currentUser?.jobs?.some(j => ['sales_manager', 'deputy-CEO'].includes(j)) ?? false;
  }

  canViewAllUsers(): boolean {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.jobs || currentUser.jobs.length === 0) return false;
    
    return currentUser.jobs.some(j => CONSTANTS.ALLOWED_ROLES.includes(j as any));
  }

  // ===== Public Utility Methods =====

  onWeekChanged(weekInfo: WeekInfo): void {
    this.selectedWeek = weekInfo;
  }

  onStatisticsMonthChange(month: string): void {
    this.currentMonth = month;
    this.updateDaysInCurrentMonth();
    this.loadStatisticsData();
  }

  onStatisticsYearChange(year: number): void {
    this.currentYear = year;
    this.updateDaysInCurrentMonth();
    this.loadStatisticsData();
  }

  onStatisticsModeChange(isKumMode: boolean): void {
    this.isKumMode = isKumMode;
    this.loadStatisticsData();
  }

  onExportStatusReport(): void {
    this.isExportingStatusReport = true;
    this.uploadService.exportStatusReport().subscribe({
      next: (blob: Blob) => {
        this.downloadStatusReportFile(blob);
        this.isExportingStatusReport = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.handleError('A státusz riport exportálása sikertelen', error);
        this.isExportingStatusReport = false;
      }
    });
  }

  private downloadStatusReportFile(blob: Blob): void {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    const fileName = `Statusz_riport_${now.getFullYear()}${month}${day}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  getCurrentDateString(): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date().toLocaleDateString('hu-HU', options);
  }

  // ===== Private Initialization Methods =====

  private initializeStats(): void {
    this.stats = this.createCallCenterStats();
    this.adminStats = this.createAdminStats();
    this.kamStats = this.createKamStats();
  }

  private initializeComponent(): void {
    this.loadCurrentDayStats();
    this.initializeCurrentMonth();
    
    if (this.isAdmin() || this.isSalesSupportLeader()) {
      this.initializeWeekData();
      this.loadCallCenterUsers();
      this.loadStatusReport();

      if (this.isAdmin()) {
        this.loadAdminStats();
      }
    }

    if (this.isKAM()) {
      this.loadKAMStats();
    }
  }

  private initializeCurrentMonth(): void {
    const now = new Date();
    this.currentYear = now.getFullYear();
    this.currentMonth = CONSTANTS.HUNGARIAN_MONTHS[now.getMonth()];
    this.daysInCurrentMonth = new Date(this.currentYear, now.getMonth() + 1, 0).getDate();
    
    if (this.isAdmin() || this.isSalesSupportLeader()) {
      setTimeout(() => this.loadStatisticsData(), 0);
    }
  }

  private initializeWeekData(): void {
    const currentDate = new Date();
    this.selectedWeek = {
      weekNumber: this.getWeekNumber(currentDate),
      startDate: this.getStartOfWeek(currentDate),
      endDate: this.getEndOfWeek(currentDate),
      season: this.getSeason(currentDate)
    };
  }

  private getSeason(date: Date): 'spring' | 'summer' | 'autumn' | 'winter' {
    const month = date.getMonth() + 1; // JavaScript months are 0-based
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  }

  // ===== Statistics Configuration Methods =====

  private createCallCenterStats(): StatCard[] {
    return [
      {
        icon: 'call_made',
        value: 0,
        label: 'Indított hívás',
        change: { value: '0%', type: 'neutral' },
        iconClass: 'call-quality',
        specialFormat: { actualValue: 0, planValue: 0 }
      },
      {
        icon: 'phone_callback',
        value: 0,
        label: 'Visszahívni',
        iconClass: 'revenue'
      },
      {
        icon: 'block',
        value: 0,
        label: 'Elutasított',
        iconClass: 'deals'
      },
      {
        icon: 'do_not_disturb',
        value: 0,
        label: 'Robinson listás',
        iconClass: 'backup'
      },
      {
        icon: 'phone_missed',
        value: 0,
        label: 'Nem vette fel',
        iconClass: 'tasks'
      },
      {
        icon: 'event_available',
        value: 0,
        label: 'Egyeztetett időpont',
        change: { value: '0%', type: 'neutral' },
        iconClass: 'appointments',
        specialFormat: { actualValue: 0, planValue: 0 }
      },
      {
        icon: 'security',
        value: 0,
        label: 'Vagyonbiztosításra egyeztetett időpont',
        change: { value: '0%', type: 'neutral' },
        iconClass: 'assessment',
        specialFormat: { actualValue: 0, planValue: 0 }
      }
    ];
  }

  private createAdminStats(): StatCard[] {
    return [
      {
        icon: 'people',
        value: 0,
        label: 'Aktív kollégák',
        iconClass: 'customers'
      },
      {
        icon: 'today',
        value: 0,
        label: 'Mai összes logolt tevékenység',
        iconClass: 'tasks'
      },
      {
        icon: 'backup',
        value: 'Nincs adat',
        label: 'Utolsó manuális backup',
        iconClass: 'backup'
      },
      {
        icon: 'phone',
        value: 0,
        label: 'Hívandó rekordok',
        iconClass: 'call-quality'
      }
    ];
  }

  private createKamStats(): StatCard[] {
    return [
      {
        icon: 'event_available',
        value: '0 / 0',
        label: 'Egyeztetett időpont',
        iconClass: 'appointments'
      },
      {
        icon: 'handshake',
        value: '0 / 0',
        label: 'Sikeres első tárgyalás',
        iconClass: 'deals'
      },
      {
        icon: 'assessment',
        value: '0 / 0',
        label: 'Sikeres felmérés',
        iconClass: 'assessment'
      },
      {
        icon: 'present_to_all',
        value: '0 / 0',
        label: 'Sikeres ajánlatbemutatás',
        iconClass: 'presentation'
      },
      {
        icon: 'check_circle',
        value: '0 / 0',
        label: 'Sikeres zárás',
        iconClass: 'closing'
      }
    ];
  }

  // ===== Data Loading Methods =====

  private loadCurrentDayStats(): void {
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser?.id) return;

    const weekInfo = this.callCenterStatsService.getCurrentWeekInfo();
    const dayIndex = this.callCenterStatsService.getCurrentDayIndex();
    
    this.callCenterStatsService.getUserWeekStats(
      currentUser.id,
      weekInfo.weekNumber,
      weekInfo.year
    ).subscribe({
      next: (data) => this.updateStatsWithData(data, dayIndex),
      error: (error) => this.handleError('Hiba a napi statisztikák betöltésekor', error)
    });
  }

  private updateStatsWithData(data: any, dayIndex: number): void {
    const actualValue = data.initiated_calls_actual[dayIndex] || 0;
    const planValue = data.initiated_calls_plan[dayIndex] || 0;
    const appointmentValue = data.appointment_scheduled_actual[dayIndex] || 0;
    const appointmentPlanValue = data.appointment_scheduled_plan[dayIndex] || 0;
    const propertyInsuranceValue = data.property_insurance_appointment_actual[dayIndex] || 0;
    const propertyInsurancePlanValue = data.property_insurance_appointment_plan[dayIndex] || 0;
    
    const percentage = this.calculatePercentage(actualValue, planValue);
    const appointmentPercentage = this.calculatePercentage(appointmentValue, appointmentPlanValue);
    const propertyInsurancePercentage = this.calculatePercentage(propertyInsuranceValue, propertyInsurancePlanValue);
    
    this.stats = [
      {
        ...this.stats[0],
        specialFormat: { actualValue, planValue },
        change: { value: `${percentage}%`, type: this.getPerformanceType(percentage) }
      },
      { ...this.stats[1], value: data.callback[dayIndex] || 0 },
      { ...this.stats[2], value: data.rejected[dayIndex] || 0 },
      { ...this.stats[3], value: data.robinson_list[dayIndex] || 0 },
      { ...this.stats[4], value: data.no_answer[dayIndex] || 0 },
      {
        ...this.stats[5],
        specialFormat: { actualValue: appointmentValue, planValue: appointmentPlanValue },
        change: { value: `${appointmentPercentage}%`, type: this.getPerformanceType(appointmentPercentage) }
      },
      {
        ...this.stats[6],
        specialFormat: { actualValue: propertyInsuranceValue, planValue: propertyInsurancePlanValue },
        change: { value: `${propertyInsurancePercentage}%`, type: this.getPerformanceType(propertyInsurancePercentage) }
      }
    ];
    
    this.cdr.detectChanges();
  }

  private loadCallCenterUsers(): void{
    this.isLoadingStats = true;
    this.authService.getCallCenterUsers().subscribe({
      next: (users: any[]) => {
        this.callCenterUsers = users;
        this.isLoadingStats = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.handleError('Hiba a call center felhasználók betöltésekor', error);
        this.callCenterUsers = [];
        this.isLoadingStats = false;
      }
    });
  }

  private loadStatusReport(): void {
    this.isLoadingStatusReport = true;
    this.uploadService.getStatusReport().subscribe({
      next: (response) => {
        this.statusReportRows = response.rows ?? [];
        this.isLoadingStatusReport = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.handleError('Hiba a státusz riport betöltésekor', error);
        this.statusReportRows = [];
        this.isLoadingStatusReport = false;
      }
    });
  }

  private updateDaysInCurrentMonth(): void {
    const monthIndex = CONSTANTS.HUNGARIAN_MONTHS.indexOf(this.currentMonth as any);
    if (monthIndex !== -1) {
      this.daysInCurrentMonth = new Date(this.currentYear, monthIndex + 1, 0).getDate();
    }
  }

  private loadStatisticsData(): void {
    this.isLoadingStatistics = true;
    
    if (!this.canViewAllUsers()) {
      this.colleaguesWithMetrics = [];
      this.isLoadingStatistics = false;
      this.cdr.markForCheck();
      return;
    }

    const roleFilter = this.isKumMode ? 'kam' : 'call_center';
    
    this.authService.getAllUsers().subscribe({
      next: (users: User[]) => {
        const colleagues = users.filter(user => 
          user.is_active && 
          user.jobs?.some(j => j === roleFilter)
        );

        this.colleaguesWithMetrics = colleagues.map((colleague, index) => ({
          colleague,
          performanceMetrics: this.generateColleagueMetrics(colleague.username, index)
        }));

        // Bulk preloading az összes kolléga adataihoz (1 API kérés az összes userhez)
        const monthIndex = CONSTANTS.HUNGARIAN_MONTHS.indexOf(this.currentMonth as any);
        const userIds = colleagues.map(c => c.id).filter((id): id is number => !!id);

        if (!this.isKumMode && userIds.length > 0 && monthIndex !== -1) {
          this.preloadedKumStatsMap = null;
          this.callCenterStatsService.getMonthStatsBulk(userIds, monthIndex + 1, this.currentYear).subscribe({
            next: (statsMap) => {
              this.preloadedStatsMap = statsMap;
              this.isLoadingStatistics = false;
              this.cdr.markForCheck();
            },
            error: () => {
              this.preloadedStatsMap = null;
              this.isLoadingStatistics = false;
              this.cdr.markForCheck();
            }
          });
        } else if (this.isKumMode && userIds.length > 0 && monthIndex !== -1) {
          this.preloadedStatsMap = null;
          this.salesStatsService.getMonthStatsBulk(userIds, monthIndex + 1, this.currentYear).subscribe({
            next: (statsMap) => {
              this.preloadedKumStatsMap = statsMap;
              this.isLoadingStatistics = false;
              this.cdr.markForCheck();
            },
            error: () => {
              this.preloadedKumStatsMap = null;
              this.isLoadingStatistics = false;
              this.cdr.markForCheck();
            }
          });
        } else {
          this.preloadedStatsMap = null;
          this.preloadedKumStatsMap = null;
          this.isLoadingStatistics = false;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        this.handleError('Hiba a statisztikai adatok betöltésekor', error);
        this.isLoadingStatistics = false;
      }
    });
  }

  private loadAdminStats(): void {
    if (!this.canViewAllUsers()) return;
    
    this.loadActiveColleagues();
    this.loadTodayInteractions();
    this.loadLastBackup();
    this.loadPendingRecords();
  }

  private loadActiveColleagues(): void {
    this.authService.getAllUsers().subscribe({
      next: (users: any[]) => {
        const activeCount = users.filter(user => user.is_active === true).length;
        this.updateAdminStat(0, activeCount);
      },
      error: (error) => this.handleError('Hiba az aktív kollégák számának betöltésekor', error)
    });
  }

  private loadTodayInteractions(): void {
    this.interactionsService.getTodayInteractionsCount().subscribe({
      next: (response) => {
        if (response.success) {
          this.updateAdminStat(1, response.count);
        }
      },
      error: (error) => this.handleError('Hiba a mai tevékenységek számának betöltésekor', error)
    });
  }

  private loadLastBackup(): void {
    this.backupService.getLatestManualBackup().subscribe({
      next: (response) => {
        const backupValue = response.success && response.latest_backup
          ? this.formatBackupDate(new Date(response.latest_backup.timestamp))
          : 'Nincs adat';
        this.updateAdminStat(2, backupValue);
      },
      error: (error) => {
        this.handleError('Hiba az utolsó manuális backup betöltésekor', error);
        this.updateAdminStat(2, 'Nincs adat');
      }
    });
  }

  private loadPendingRecords(): void {
    this.uploadService.getCallableRecordsCount().subscribe({
      next: (response) => {
        if (response.success) {
          this.updateAdminStat(3, response.count);
        }
      },
      error: (error) => this.handleError('Hiba a hívandó rekordok számának betöltésekor', error)
    });
  }

  private loadKAMStats(): void {
    // TODO: Replace with actual backend endpoints
    this.kamStats = [
      { ...this.kamStats[0], value: '3 / 2' },
      { ...this.kamStats[1], value: '2 / 1' },
      { ...this.kamStats[2], value: '5 / 3' },
      { ...this.kamStats[3], value: '4 / 2' },
      { ...this.kamStats[4], value: '3 / 1' }
    ];
    
    this.cdr.detectChanges();
  }

  // ===== Utility Methods =====

  private calculatePercentage(actual: number, plan: number): number {
    return plan > 0 ? Math.round((actual / plan) * 100) : 0;
  }

  private getPerformanceType(percentage: number): 'positive' | 'negative' | 'neutral' {
    if (percentage >= CONSTANTS.FULL_PERFORMANCE_THRESHOLD) return 'positive';
    if (percentage >= CONSTANTS.MINIMUM_PERFORMANCE_THRESHOLD) return 'neutral';
    return 'negative';
  }

  private updateAdminStat(index: number, value: string | number): void {
    this.adminStats = [
      ...this.adminStats.slice(0, index),
      { ...this.adminStats[index], value },
      ...this.adminStats.slice(index + 1)
    ];
    this.cdr.detectChanges();
  }

  private formatBackupDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}.${month}.${day} ${hour}:${minute}`;
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.snackBar.open(`${message}: ${error.message || error}`, 'Bezárás', {
      duration: CONSTANTS.ERROR_DURATION,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  // ===== Week Calculation Methods =====

  private getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }

  private getStartOfWeek(date: Date): Date {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  }

  private getEndOfWeek(date: Date): Date {
    const startOfWeek = this.getStartOfWeek(new Date(date));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return endOfWeek;
  }

  // ===== Mock Data Generation =====

  private generateColleagueMetrics(colleagueName: string, index: number): PerformanceMetric[] {
    const metrics: PerformanceMetric[] = [
      {
        name: 'Hívások száma',
        actualValues: this.generateDailyValues(30, 50),
        planValues: this.generateDailyValues(35, 45),
        percentageValues: []
      },
      {
        name: 'Sikeres kapcsolatok',
        actualValues: this.generateDailyValues(15, 25),
        planValues: this.generateDailyValues(18, 22),
        percentageValues: []
      },
      {
        name: 'Időpontfoglalások',
        actualValues: this.generateDailyValues(5, 12),
        planValues: this.generateDailyValues(8, 10),
        percentageValues: []
      }
    ];

    // Calculate percentages
    metrics.forEach(metric => {
      metric.percentageValues = metric.planValues.map((planValue: number, dayIndex: number) => 
        this.calculatePercentage(metric.actualValues[dayIndex], planValue)
      );
    });

    return metrics;
  }

  private generateDailyValues(min: number, max: number): number[] {
    const values: number[] = [];
    for (let i = 0; i < this.daysInCurrentMonth; i++) {
      const dayOfWeek = new Date(this.currentYear, new Date().getMonth(), i + 1).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      const adjustedMin = isWeekend ? Math.round(min * CONSTANTS.WEEKEND_MULTIPLIER) : min;
      const adjustedMax = isWeekend ? Math.round(max * CONSTANTS.WEEKEND_MAX_MULTIPLIER) : max;
      
      values.push(Math.floor(Math.random() * (adjustedMax - adjustedMin + 1)) + adjustedMin);
    }
    return values;
  }
}